"use client";

import Image from "next/image";
import React from "react";

import { Avatar } from "@nextui-org/react";
import cn from "clsx";
import { useLocale, useTranslations } from "next-intl";

import { Card } from "@/components/ui/Card";
import Loader from "@/components/ui/Loader";
// Custom hooks
import EditIcon from "@/icons/edit.svg";
import AttachmentRedIcon from "@/icons/icon_attachment_red.svg";
import ImageRedIcon from "@/icons/icon_image_red.svg";
import UploadGrayIcon from "@/icons/icon_upload_gray.svg";
import SendIcon from "@/icons/send-icon-figma.svg";
import { useSession } from "@/providers/SessionProvider";
import {
  type CommentItem,
  type CommentUser,
} from "@/services/realtor-comments.service";
import { createLogger } from "@/utils/logger";

import { useCommentActions, useCommentData } from "./hooks";

const logger = createLogger("PropertyComments");

// Legacy interface for backward compatibility during migration
export interface ICommentItem extends Omit<CommentItem, "userId"> {
  // Legacy mappings for existing component code
  message: string; // Maps to content
  timestamp: string; // Maps to createdAt
  userId: CommentUser; // Maps to user object for legacy code
  replies?: ICommentItem[]; // Nested replies
}

interface IPropertyCommentsProps extends React.HTMLAttributes<HTMLDivElement> {
  propertyId: string;
  comments?: ICommentItem[];
  isLoading?: boolean;
  onSendMessage?: (message: string) => void;
  onUploadFile?: (file: File) => void;
}

export const PropertyComments: React.FC<IPropertyCommentsProps> = (props) => {
  const {
    propertyId,
    comments = [],
    isLoading = false,
    onSendMessage,
    onUploadFile,
    className,
  } = props;
  const t = useTranslations();
  const tRealtor = useTranslations("realtor");
  const locale = useLocale();

  const { user, isAuthenticated } = useSession();

  // Restore original functionality
  const { displayComments, isListLoading } = useCommentData({
    propertyId,
    comments,
  });
  const {
    message,
    setMessage,
    handleSendMessage,
    handleFileUpload,
    handleImageUpload,
    pendingFiles,
    pendingImages,
    removePendingFile,
    removePendingImage,
    clearPendingFiles,
    handleCreateReply,
  } = useCommentActions({ propertyId, onSendMessage, onUploadFile });

  // Reply functionality
  const [replyingTo, setReplyingTo] = React.useState<string | null>(null);
  const [replyMessage, setReplyMessage] = React.useState<string>("");

  const handleReply = (commentId: string) => {
    setReplyingTo(commentId);
    setReplyMessage("");
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyMessage("");
  };

  const handleSendReply = () => {
    if (replyMessage.trim() && replyingTo) {
      logger.debug("Sending reply to:", replyingTo, "Message:", replyMessage);

      // Use the createReply function that we'll add to useCommentActions
      if (handleCreateReply) {
        handleCreateReply(replyMessage.trim(), replyingTo);

        // Clear reply state
        setReplyingTo(null);
        setReplyMessage("");
      }
    }
  };

  // Format timestamp according to Figma design and locale
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const commentDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    const timeStr = date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (commentDate.getTime() === today.getTime()) {
      return `${t("comments.Today")} ${timeStr}`;
    } else {
      const dateStr = date.toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      return `${dateStr} ${timeStr}`;
    }
  };

  // Render a single comment with replies
  const renderComment = (comment: ICommentItem, isReply = false) => {
    if (!comment || !comment._id) return null;

    return (
      <div
        key={comment._id}
        className={cn("mb-[2.4rem]", isReply && "ml-[6rem] mb-[1.6rem]")}
      >
        <div
          className={cn(
            "flex items-center mb-[0.8rem]",
            isReply && "mb-[0.6rem]",
          )}
        >
          <div
            className={cn(
              "text-[1.5rem] font-normal text-black opacity-65 pr-[2rem] font-geometria",
              isReply && "text-[1.3rem] opacity-50",
            )}
          >
            {formatTimestamp(comment.createdAt).split(" ")[0]}
          </div>
          <div
            className={cn(
              "text-[1.5rem] font-normal text-black opacity-65 font-geometria",
              isReply && "text-[1.3rem] opacity-50",
            )}
          >
            {formatTimestamp(comment.createdAt).split(" ")[1] || ""}
          </div>
        </div>
        <div
          className={cn(
            "relative bg-[#f8f8f8] rounded-[1.5rem] p-[2rem] mb-[2.4rem]",
            comment.isCurrentUser &&
              "bg-[#f3f3f3] border border-[rgba(207,20,9,0.1)]",
            isReply &&
              "bg-[#fafafa] border-l-[0.4rem] border-l-[rgba(207,20,9,0.3)] border-r border-t border-b border-[rgba(207,20,9,0.1)] mb-[1.6rem]",
          )}
        >
          <div
            className={cn(
              "absolute left-[2rem] top-[2rem] w-[7.5rem] h-[7.5rem] rounded-full overflow-hidden bg-[#e6e6e6]",
              isReply && "w-[5rem] h-[5rem]",
            )}
          >
            <Avatar
              src={
                comment.user?.avatar || comment.user?.profilePhoto || undefined
              }
              name={comment.user?.displayName || "Unknown User"}
              className="w-full h-full"
            />
          </div>

          <div className={cn("ml-[11.5rem]", isReply && "ml-[8rem]")}>
            <div className={cn("mb-[1.6rem]", isReply && "mb-[1.2rem]")}>
              <h4
                className={cn(
                  "text-[1.8rem] font-medium text-gray-1200 mb-[0.4rem] font-geometria",
                  isReply && "text-[1.6rem]",
                )}
              >
                {comment.user?.displayName || "Unknown User"}
              </h4>
              <div className="flex items-center gap-[1.6rem]">
                <span
                  className={cn(
                    "text-[1.6rem] font-normal text-gray-1200 font-geometria",
                    isReply && "text-[1.4rem]",
                  )}
                >
                  {comment.user?.primaryRole || "user"}
                </span>
                {comment.user?.location && (
                  <span
                    className={cn(
                      "text-[1.5rem] font-normal text-gray-1200 font-geometria before:content-['•'] before:mr-[0.8rem] before:text-gray-500",
                      isReply && "text-[1.3rem]",
                    )}
                  >
                    {comment.user.location}
                  </span>
                )}
              </div>
            </div>

            <div className="py-[0.8rem] mb-[1.6rem]">
              <p
                className={cn(
                  "text-[1.5rem] font-normal text-gray-1200 opacity-65 leading-[2.2rem] font-geometria whitespace-pre-wrap",
                  isReply && "text-[1.4rem] leading-[2rem]",
                )}
              >
                {comment.content}
              </p>

              {comment.images && comment.images.length > 0 && (
                <div className="flex gap-[1.6rem] mt-[1.6rem] flex-wrap">
                  {comment.images?.slice(0, 2).map((image) => (
                    <div
                      key={image._id || Math.random()}
                      className="w-[15rem] h-[15rem] md:w-[12rem] md:h-[12rem] md:rounded-[1.5rem] rounded-[2rem] overflow-hidden bg-[#e6e6e6] relative cursor-pointer transition-all duration-300 ease-out border-2 border-transparent shadow-[0_0.2rem_0.8rem_rgba(0,0,0,0.1)] hover:scale-[1.08] hover:border-red hover:shadow-[0_0.8rem_2.4rem_rgba(207,20,9,0.25)] focus:outline-2 focus:outline-red focus:outline-offset-2 group"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logger.debug("Image clicked:", image?.url);
                        if (image?.url) {
                          logger.debug("Opening image:", image.url);
                          window.open(
                            image.url,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        } else {
                          logger.debug("No image URL available");
                        }
                      }}
                      style={{ cursor: image?.url ? "pointer" : "default" }}
                    >
                      {image?.url ? (
                        <Image
                          src={image.url}
                          alt={`Comment image ${image._id || "unknown"}`}
                          width={64}
                          height={64}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                            target.nextElementSibling?.classList.remove(
                              "hidden",
                            );
                          }}
                          onLoad={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.nextElementSibling?.classList.add("hidden");
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#f0f0f0] text-[#9e9e9e] text-[1.2rem] font-medium font-geometria">
                          📷
                        </div>
                      )}
                      <div
                        className={cn(
                          "absolute inset-0 flex items-center justify-content bg-[#f0f0f0] text-[#9e9e9e] text-[1.2rem] font-medium font-geometria",
                          image?.url ? "hidden" : "",
                        )}
                      >
                        📷
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 transition-opacity duration-300 ease-out flex items-center justify-center backdrop-blur-[0.1rem] group-hover:opacity-100">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M10 3C6.134 3 3 6.134 3 10C3 13.866 6.134 17 10 17C13.866 17 17 13.866 17 10C17 6.134 13.866 3 10 3ZM10 15C7.239 15 5 12.761 5 10C5 7.239 7.239 5 10 5C12.761 5 15 7.239 15 10C15 12.761 12.761 15 10 15Z"
                            fill="white"
                          />
                          <path
                            d="M10 7V13M7 10H13"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    </div>
                  ))}
                  {comment.images?.filter((img) => img?.url?.trim()).length >
                    2 && (
                    <div
                      className="w-[10rem] h-[10rem] md:w-[12rem] md:h-[12rem] md:rounded-[1.5rem] rounded-[2rem] flex items-center justify-center cursor-pointer transition-all duration-300 ease-out border-2 border-transparent bg-gradient-to-br from-red to-[#b01208] shadow-[0_0.2rem_0.8rem_rgba(0,0,0,0.1)] hover:scale-[1.08] hover:shadow-[0_0.8rem_2.4rem_rgba(207,20,9,0.4)] focus:outline-2 focus:outline-red focus:outline-offset-2"
                      onClick={() => {
                        // Открываем третье изображение (индекс 2)
                        const thirdImage = comment.images?.filter((img) =>
                          img?.url?.trim(),
                        )[2];
                        if (thirdImage?.url) {
                          window.open(
                            thirdImage.url,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <span className="text-[1.4rem] md:text-[1.2rem] font-bold text-white text-center font-geometria text-shadow-[0_0.1rem_0.2rem_rgba(0,0,0,0.3)] leading-[1.2]">
                        +{" "}
                        {comment.images?.filter((img) => img?.url?.trim())
                          .length - 2}{" "}
                        {t("comments.photos")}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {comment.attachments && comment.attachments.length > 0 && (
                <div className="mt-[1.6rem]">
                  {comment.attachments.map((file) => (
                    <div
                      key={file._id}
                      className="inline-flex items-center gap-[0.8rem] p-[1.2rem] border border-[#989898] rounded-[1.5rem] w-[15.2rem] h-[4.5rem] cursor-pointer transition-all duration-200 ease-out mr-[1rem] mb-[1rem] hover:border-red hover:bg-[rgba(207,20,9,0.05)] hover:scale-[1.02]"
                      onClick={() => {
                        if (file.url) {
                          window.open(
                            file.url,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                      }}
                      style={{ cursor: file.url ? "pointer" : "default" }}
                    >
                      <AttachmentRedIcon className="w-[2rem] h-[2.1rem] text-red flex-shrink-0" />
                      <span className="text-[1.5rem] font-normal text-gray-1200 opacity-65 font-geometria overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                        {file.originalName || file.filename}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isReply && (
              <div className="flex items-center justify-end">
                <button
                  onClick={() => handleReply(comment._id)}
                  className="bg-transparent border-none text-red text-[1.4rem] font-normal font-geometria py-[0.4rem] px-[1.2rem] cursor-pointer transition-colors duration-200 ease-out rounded-[0.6rem] hover:bg-[#fef2f2]"
                >
                  {t("comments.Reply")}
                </button>
              </div>
            )}

            {replyingTo === comment._id && (
              <div className="mt-[1.6rem] p-[1.6rem] bg-[#f0f0f0] rounded-[1rem]">
                <div className="w-full mb-[1.2rem]">
                  <input
                    type="text"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder={t("comments.Write a reply...")}
                    className="w-full h-[4rem] px-[1.2rem] border border-[#ececec] rounded-[1rem] bg-white text-[1.4rem] font-normal text-[#4d4d4d] font-geometria placeholder:text-[#9e9e9e] focus:outline-none focus:border-red"
                  />
                </div>
                <div className="flex gap-[0.8rem]">
                  <button
                    onClick={handleSendReply}
                    className="bg-red text-white border-none text-[1.4rem] font-medium py-[0.6rem] px-[1.6rem] rounded-[0.6rem] cursor-pointer transition-colors duration-200 ease-out font-geometria hover:bg-[#b01208]"
                  >
                    {t("comments.Send")}
                  </button>
                  <button
                    onClick={handleCancelReply}
                    className="bg-transparent text-gray-600 border-none text-[1.4rem] font-normal py-[0.6rem] px-[1.6rem] rounded-[0.6rem] cursor-pointer transition-colors duration-200 ease-out font-geometria hover:bg-[#f5f5f5]"
                  >
                    {t("comments.Cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Render replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-[1.6rem] ml-0">
            {comment.replies.map((reply) => renderComment(reply, true))}
          </div>
        )}

        <hr className="h-[0.1rem] bg-[#e5e5e5] my-[2.4rem] border-none" />
      </div>
    );
  };

  return (
    <section className={cn("", className)}>
      <h2
        className={cn(
          "object-detail__section-header",
          "mb-[2rem] text-[2.5rem] font-medium text-gray-1200 font-geometria",
        )}
      >
        {tRealtor("metadataComments")}
      </h2>

      <div className="relative p-0 bg-white rounded-[2.5rem] shadow-[0.5rem_0.5rem_2.5rem_1.5rem_rgba(235,235,235,0.25)] overflow-visible">
        {isLoading || isListLoading ? (
          <div className="flex flex-col items-center justify-center py-[6rem]">
            <Loader />
            <p className="mt-[1.6rem] text-[1.5rem] font-normal font-geometria text-gray-500">
              {t("timeline.LoadingHistory")}
            </p>
          </div>
        ) : (
          <div className="py-[2.4rem] px-[3rem] pb-[16rem]">
            {displayComments.length === 0 ? (
              <div className="text-center py-[8rem]">
                <p className="text-[#444444] text-[1.6rem] font-normal font-geometria">
                  {t("comments.No comments yet")}
                </p>
              </div>
            ) : (
              displayComments
                .map((comment) => renderComment(comment))
                .filter(Boolean)
            )}
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-[4rem] bg-white rounded-b-[2.5rem] border-t border-[#ececec]">
          {/* Pending Files Preview */}
          {(pendingFiles.length > 0 || pendingImages.length > 0) && (
            <div className="mb-[2rem] p-[1.6rem] bg-[#f8f9fa] rounded-[1.5rem] border border-[#e9ecef]">
              <div className="flex justify-between items-center mb-[1.2rem]">
                <span className="text-[1.4rem] font-medium text-gray-1200 font-geometria">
                  {t("comments.Selected files")} (
                  {pendingFiles.length + pendingImages.length})
                </span>
                <button
                  onClick={clearPendingFiles}
                  className="bg-transparent border-none text-red text-[1.2rem] font-normal font-geometria cursor-pointer py-[0.4rem] px-[0.8rem] rounded-[0.6rem] transition-colors duration-200 ease-out hover:bg-[#fef2f2]"
                  type="button"
                >
                  {t("comments.Clear all")}
                </button>
              </div>

              {/* Pending Images */}
              {pendingImages.length > 0 && (
                <div className="flex flex-wrap gap-[1.2rem] mb-[1.2rem]">
                  {pendingImages.map((file, index) => (
                    <div
                      key={`pending-image-${index}`}
                      className="flex items-center gap-[1rem] p-[0.8rem] bg-white rounded-[1rem] border border-[#e9ecef] min-w-[20rem]"
                    >
                      <Image
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        width={64}
                        height={64}
                        className="w-[4rem] h-[4rem] rounded-[0.8rem] object-cover bg-[#f0f0f0]"
                        onLoad={(e) => {
                          // Clean up object URL when component unmounts
                          const img = e.target as HTMLImageElement;
                          const cleanup = () => URL.revokeObjectURL(img.src);
                          setTimeout(cleanup, 1000);
                        }}
                      />
                      <div className="flex-1 flex flex-col">
                        <div className="text-[1.3rem] font-normal text-gray-1200 font-geometria overflow-hidden text-ellipsis whitespace-nowrap max-w-[12rem]">
                          {file.name}
                        </div>
                        <div className="text-[1.1rem] font-normal text-[#6c757d] font-geometria">
                          {(file.size / 1024 / 1024).toFixed(1)} MB
                        </div>
                      </div>
                      <button
                        onClick={() => removePendingImage(index)}
                        className="bg-transparent border-none text-[#dc3545] text-[1.4rem] cursor-pointer p-[0.4rem] rounded-full w-[2.4rem] h-[2.4rem] flex items-center justify-center transition-colors duration-200 ease-out hover:bg-[#f8d7da]"
                        type="button"
                        aria-label={t("comments.Remove image")}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending Files */}
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-[1.2rem]">
                  {pendingFiles.map((file, index) => (
                    <div
                      key={`pending-file-${index}`}
                      className="flex items-center gap-[1rem] p-[0.8rem] bg-white rounded-[1rem] border border-[#e9ecef] min-w-[20rem]"
                    >
                      <AttachmentRedIcon className="w-[2rem] h-[2.1rem] text-red" />
                      <div className="flex-1 flex flex-col">
                        <div className="text-[1.3rem] font-normal text-gray-1200 font-geometria overflow-hidden text-ellipsis whitespace-nowrap max-w-[12rem]">
                          {file.name}
                        </div>
                        <div className="text-[1.1rem] font-normal text-[#6c757d] font-geometria">
                          {(file.size / 1024 / 1024).toFixed(1)} MB
                        </div>
                      </div>
                      <button
                        onClick={() => removePendingFile(index)}
                        className="bg-transparent border-none text-[#dc3545] text-[1.4rem] cursor-pointer p-[0.4rem] rounded-full w-[2.4rem] h-[2.4rem] flex items-center justify-center transition-colors duration-200 ease-out hover:bg-[#f8d7da]"
                        type="button"
                        aria-label={t("comments.Remove file")}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Main input row with integrated send button */}
          <div className="flex items-center gap-[1.2rem] mb-[2rem] bg-white border border-[#ececec] rounded-[2rem] h-[5.2rem] px-[0.6rem] transition-colors duration-200 ease-out shadow-[0.5rem_0.5rem_2.5rem_1.5rem_rgba(235,235,235,0.25)] focus-within:border-[#cf1409] focus-within:shadow-[0_0_0_0.1rem_rgba(207,20,9,0.1)]">
            <div className="flex-1 flex items-center gap-[1.2rem] px-[1.8rem]">
              <EditIcon className="w-[1.8rem] h-[1.8rem] text-[#323232] flex-shrink-0" />
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={t("comments.Write a message")}
                className="flex-1 h-full p-0 border-none bg-transparent text-[1.4rem] font-normal text-[#4d4d4d] font-geometria placeholder:text-[#9e9e9e] focus:outline-none"
              />
            </div>
            <button
              onClick={handleSendMessage}
              className="h-[5.2rem] bg-white text-[#1b1b1b] border border-[#cf1409] rounded-[2rem] px-[2rem] text-[1.6rem] font-medium font-geometria flex items-center justify-center gap-[0.6rem] cursor-pointer transition-all duration-200 ease-out flex-shrink-0 min-w-[11.6rem] hover:bg-[#fef2f2] hover:-translate-y-[0.1rem] active:translate-y-0 active:scale-[0.98]"
              data-testid="send-message-button"
            >
              {t("comments.Send")}
              <SendIcon className="w-[2rem] h-[2rem] text-[#de4651]" />
            </button>
          </div>

          {/* Upload buttons row */}
          <div className="flex gap-[1.6rem] items-center">
            <label className="flex items-center gap-[0.8rem] h-[5.2rem] px-[2rem] bg-white border border-[#ececec] rounded-[2rem] text-[1.4rem] font-normal text-[#4d4d4d] cursor-pointer transition-all duration-200 ease-out font-geometria shadow-[0.5rem_0.5rem_2.5rem_1.5rem_rgba(235,235,235,0.25)] hover:border-[#cf1409] hover:bg-[#fef9f9] hover:text-[#cf1409] hover:-translate-y-[0.1rem] hover:[&>svg]:text-[#cf1409] hover:[&>svg]:scale-[1.05] active:translate-y-0 active:scale-[0.98]">
              <UploadGrayIcon className="w-[1.86rem] h-[2.34rem] text-[#323232] flex-shrink-0 transition-all duration-200 ease-out" />
              {t("comments.Upload file")}
              <input
                type="file"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            <label className="flex items-center justify-center min-w-[5.2rem] h-[5.2rem] px-[1.6rem] bg-white border border-[#ececec] rounded-[2rem] text-[1.4rem] font-normal text-[#4d4d4d] cursor-pointer transition-all duration-200 ease-out font-geometria shadow-[0.5rem_0.5rem_2.5rem_1.5rem_rgba(235,235,235,0.25)] hover:border-[#cf1409] hover:bg-[#fef9f9] hover:text-[#cf1409] hover:-translate-y-[0.1rem] hover:[&>svg]:text-[#cf1409] hover:[&>svg]:scale-[1.05] active:translate-y-0 active:scale-[0.98]">
              <ImageRedIcon className="w-[1.724rem] h-[1.736rem] text-[#cf1409] flex-shrink-0 transition-all duration-200 ease-out" />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PropertyComments;
